import React, { useEffect, useRef, useState } from 'react';
import "./WorkyMainDemo.css";
import { SquarePen, Bell, User, PanelLeft, Plus, Layers, Users, LayoutGrid, CreditCard, FileText, BarChart2, UserPlus, Store, CheckSquare, ChevronDown, RefreshCw, Sparkles, ChartNoAxesCombined, SquaresExclude } from 'lucide-react';
import { BsStars } from "react-icons/bs";
import { WorkyMainDemoOpsShiftManagementScreen } from './WorkyMainDemoOpsShiftManagementScreen/WorkyMainDemoOpsShiftManagementScreen';
import { WorkyMainDemoOpsWorkforceScreen } from './WorkyMainDemoOpsWorkforceScreen/WorkyMainDemoOpsWorkforceScreen';
import { WorkyMainDemoOpsPayrollScreen } from './WorkyMainDemoOpsPayrollScreen/WorkyMainDemoOpsPayrollScreen';
import { DemoAnimationOverlay } from './DemoAnimationOverlay/DemoAnimationOverlay';
import { ShiftApprovalPanel } from './DemoAnimationOverlay/ShiftApprovalPanel';
import { NewShiftPanel } from './DemoAnimationOverlay/NewShiftPanel';
import { WorkerProfilePanel } from './DemoAnimationOverlay/WorkerProfilePanel';
import { InvoicePdfPanel } from './DemoAnimationOverlay/InvoicePdfPanel';
import { WorkyMainDemoOpsClientInvoicesScreen } from './WorkyMainDemoOpsClientInvoicesScreen/WorkyMainDemoOpsClientInvoicesScreen';
import { useShiftApprovalDemoAnimation, ActiveDemoScreen } from './DemoAnimationOverlay/useShiftApprovalDemoAnimation';
import type { ShiftApprovalWorker, ShiftApprovalShift } from './DemoAnimationOverlay/ShiftApprovalPanel';
import type { NewShiftWorker } from './DemoAnimationOverlay/NewShiftPanel';
import { useIsMobile } from '../../../custom-hooks/useIsMobile';
// ── Panel data constants ────────────────────────────────────────────────────

const PRIYA_WORKER: ShiftApprovalWorker = {
    name: 'Priya Patel',
    initials: 'PP',
    avatarGradient: 'linear-gradient(135deg, #6d28d9, #a78bfa)',
    role: 'PSW · Healthcare',
};

const PRIYA_SHIFT: ShiftApprovalShift = {
    timeRange: '4PM – 12AM',
    taskName: 'PSW – Memory',
    location: 'Sunnybrook HSC',
    dateRange: 'Mar 20 – Mar 30',
};

const DAVID_WORKER: NewShiftWorker = {
    name: 'David Chen',
    initials: 'DC',
    avatarGradient: 'linear-gradient(135deg, #0e7490, #22d3ee)',
    role: 'Physiotherapist · Healthcare',
};

// ── Component ───────────────────────────────────────────────────────────────

const WorkyMainDemo = (): React.ReactElement => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isInViewport, setIsInViewport] = useState<boolean>(false);
    const isMobile = useIsMobile();

    useEffect((): (() => void) | void => {
        const containerElement: HTMLDivElement | null = containerRef.current;
        if (!containerElement) return undefined;

        const observer: IntersectionObserver = new IntersectionObserver(
            (entries: readonly IntersectionObserverEntry[]): void => {
                const firstEntry: IntersectionObserverEntry | undefined = entries[0];
                setIsInViewport(Boolean(firstEntry?.isIntersecting));
            },
            {
                threshold: 0.1,
            }
        );

        observer.observe(containerElement);

        return (): void => {
            observer.disconnect();
        };
    }, []);

    const animState = useShiftApprovalDemoAnimation(containerRef, isInViewport);

    const isShiftMgmtActive: boolean = animState.activeNavItem === 'shift-management';
    const isWorkforceActive: boolean = animState.activeNavItem === 'workforce';
    const isPayrollActive: boolean = animState.activeNavItem === 'worky-pay';
    const isInvoicesActive: boolean = animState.activeNavItem === 'client-invoices';

    return (
        <div className='wmd-container' ref={containerRef}>
            <div className='wmd-t'>
                <div className='wmd-tl'>
                    <div className='wmd-tl-btn wmd-tl-btn-close'></div>
                    <div className='wmd-tl-btn wmd-tl-btn-minimize'></div>
                    <div className='wmd-tl-btn wmd-tl-btn-maximize'></div>
                    <div className='wmd-tl-icon'><PanelLeft size={15} color={'var(--launch-highlight)'} /></div>
                </div>

                <div className='wmd-tm'>
                    <BsStars size={15}/>
                    Find me the best agency management system ... Worky!
                </div>

                <div className='wmd-tr'>
                    <div className='wmd-tr-btn'><SquarePen size={13} /></div>
                    <div className='wmd-tr-btn'><Bell size={13} /></div>
                    <div className='wmd-tr-name-wrapper'>
                        <div className='wmd-tr-name'>
                            <div className='wmd-tr-name-icon'><User size={12} /></div>
                            <div className='wmd-tr-name-text'>
                                <span className='wmd-tr-name-text-name'>John Doe</span>
                                <span className='wmd-tr-name-text-title'>Agency Admin</span>
                            </div>
                        </div>
                        {/* <div className='wmd-tr-name-dropdown'>
                            <button className='wmd-tr-name-dropdown-btn wmd-tr-name-dropdown-btn-secondary'>Learn More</button>
                            <button className='wmd-tr-name-dropdown-btn wmd-tr-name-dropdown-btn-primary'>Contact Sales</button>
                        </div> */}
                    </div>
                </div>
            </div>

            <div className='wmd-b'>
                {isMobile ? null:
                <div className='wmd-bl'>
                    <div className='wmd-bl-t'>
                        <div className='wmd-bl-t-worky'>worky</div>
                    </div>

                    <div className='wmd-bl-b-nav-holder'>
                        <div className='wmd-bl-b-nav-section'>
                            <div className='wmd-bl-b-nav-section-title'>
                                OPERATIONS
                                <ChevronDown size={10} className='wmd-bl-b-nav-section-chevron' />
                            </div>

                            {/* Phase 1+2 target: Shift Management nav */}
                            <div
                                data-demo-target='nav-shift-management'
                                className={`wmd-bl-b-nav-section-item${isShiftMgmtActive ? ' wmd-bl-b-nav-section-item--active' : ''}`}
                            >
                                <Layers size={13} className={`wmd-nav-icon${isShiftMgmtActive ? ' wmd-nav-icon--active' : ''}`} />
                                Shift Management
                            </div>

                            <div className='wmd-bl-b-nav-section-item'>
                                <Plus size={13} className='wmd-nav-icon' />
                                New Shift
                            </div>

                            {/* Phase 3 target: Workforce nav */}
                            <div
                                data-demo-target='nav-workforce'
                                className={`wmd-bl-b-nav-section-item${isWorkforceActive ? ' wmd-bl-b-nav-section-item--active' : ''}`}
                            >
                                <Users size={13} className={`wmd-nav-icon${isWorkforceActive ? ' wmd-nav-icon--active' : ''}`} />
                                Workforce
                            </div>

                            <div className='wmd-bl-b-nav-section-item'>
                                <LayoutGrid size={13} className='wmd-nav-icon' />
                                Client Management
                            </div>
                            {/* Phase 4 target: Worky Pay nav */}
                            <div
                                data-demo-target='nav-worky-pay'
                                className={`wmd-bl-b-nav-section-item${isPayrollActive ? ' wmd-bl-b-nav-section-item--active' : ''}`}
                            >
                                <CreditCard size={13} className={`wmd-nav-icon${isPayrollActive ? ' wmd-nav-icon--active' : ''}`} />
                                Worky Pay
                            </div>
                            {/* Phase 5 target: Client Invoices nav */}
                            <div
                                data-demo-target='nav-client-invoices'
                                className={`wmd-bl-b-nav-section-item${isInvoicesActive ? ' wmd-bl-b-nav-section-item--active' : ''}`}
                            >
                                <FileText size={13} className={`wmd-nav-icon${isInvoicesActive ? ' wmd-nav-icon--active' : ''}`} />
                                Client Invoices
                            </div>
                            <div className='wmd-bl-b-nav-section-item'>
                                <BarChart2 size={13} className='wmd-nav-icon' />
                                Reporting
                            </div>
                        </div>

                        <div className='wmd-bl-b-nav-section'>
                            <div className='wmd-bl-b-nav-section-title'>
                                SALES
                                <ChevronDown size={10} className='wmd-bl-b-nav-section-chevron' />
                            </div>
                            <div className='wmd-bl-b-nav-section-item'>
                                <RefreshCw size={13} className='wmd-nav-icon' />
                                Leads Pro
                            </div>
                            <div className='wmd-bl-b-nav-section-item'>
                                <ChartNoAxesCombined size={13} className='wmd-nav-icon' />
                                Team Tracker
                            </div>
                            <div className='wmd-bl-b-nav-section-item'>
                                <CheckSquare size={13} className='wmd-nav-icon' />
                                Tasks
                            </div>
                        </div>

                        <div className='wmd-bl-b-nav-section'>
                            <div className='wmd-bl-b-nav-section-title'>
                                MARKETPLACE
                                <ChevronDown size={10} className='wmd-bl-b-nav-section-chevron' />
                            </div>
                            <div className='wmd-bl-b-nav-section-item'>
                                <Store size={13} className='wmd-nav-icon' />
                                Marketplace
                            </div>
                            <div className='wmd-bl-b-nav-section-item'>
                                <SquaresExclude size={13} className='wmd-nav-icon' />
                                Agency Finder
                            </div>
                            <div className='wmd-bl-b-nav-section-item'>
                                <Sparkles size={13} className='wmd-nav-icon' />
                                Worky AI
                            </div>
                        </div>

                        
                    </div>
                </div>}

                {/* Content area — switches between screens */}
                <div className={isMobile?'wmd-br-mobile':'wmd-br'}>
                    {animState.activeScreen === ActiveDemoScreen.WORKFORCE ? (
                        <WorkyMainDemoOpsWorkforceScreen />
                    ) : animState.activeScreen === ActiveDemoScreen.PAYROLL ? (
                        <WorkyMainDemoOpsPayrollScreen
                            isFirstCycleExpanded={animState.payrollCycleExpanded}
                        />
                    ) : animState.activeScreen === ActiveDemoScreen.INVOICES ? (
                        <WorkyMainDemoOpsClientInvoicesScreen
                            isKingstonExpanded={animState.isKingstonExpanded}
                        />
                    ) : (
                        <WorkyMainDemoOpsShiftManagementScreen />
                    )}
                </div>
            </div>

            {/* ── Single animation overlay covering the full browser mockup ─── */}
            <DemoAnimationOverlay
                cursorPos={animState.cursorPos}
                cursorTransitionMs={animState.cursorTransitionMs}
                isClicking={animState.isClicking}
                isVisible={animState.isVisible}
            >
                {/* Phase 1 — Shift Approval */}
                <ShiftApprovalPanel
                    isVisible={animState.showApprovalPopup}
                    worker={PRIYA_WORKER}
                    shift={PRIYA_SHIFT}
                    typedNotes={animState.approvalTypedNotes}
                    isNotesFocused={animState.isApprovalNotesFocused}
                    showSuccess={animState.showApprovalSuccess}
                />

                {/* Phase 2 — New Shift Creation */}
                <NewShiftPanel
                    isVisible={animState.showNewShiftPopup}
                    worker={DAVID_WORKER}
                    dateRange='Mar 30 – Apr 15'
                    isTimeDropdownOpen={animState.isTimeDropdownOpen}
                    selectedTime={animState.selectedTime}
                    isOrgDropdownOpen={animState.isOrgDropdownOpen}
                    selectedOrg={animState.selectedOrg}
                    typedInstructions={animState.newShiftTypedInstructions}
                    isInstructionsFocused={animState.isInstructionsFocused}
                    showSuccess={animState.showNewShiftSuccess}
                />

                {/* Phase 3b — Worker Profile */}
                <WorkerProfilePanel
                    isVisible={animState.showWorkerProfilePanel}
                    wpIsEditingRate={animState.wpIsEditingRate}
                    wpEditRateText={animState.wpEditRateText}
                    wpFirstRateDisplay={animState.wpFirstRateDisplay}
                    wpDeductionCount={animState.wpDeductionCount}
                />

                {/* Phase 5 — Invoice PDF */}
                <InvoicePdfPanel
                    isVisible={animState.showInvoicePdf}
                    showSuccess={animState.invoiceSentSuccess}
                />
            </DemoAnimationOverlay>

        </div>
    );
};


export { WorkyMainDemo };
